import type { ApolloError } from '@apollo/client/errors';

import { json } from './server/utils/json';

export const notFound = (slug: string | undefined) =>
  json({ slug }, { status: 404, statusText: 'Not found' });

// Helper function to check if error is ApolloError
const isApolloError = (err: Error | ApolloError): err is ApolloError =>
  'graphQLErrors' in err || 'networkError' in err;

// Helper function to determine if debug info should be included
const shouldIncludeDebugInfo = (): boolean =>
  process.env.NODE_ENV === 'development';

// Type for network errors with additional properties
interface NetworkErrorWithStatus {
  name: string;
  message: string;
  statusCode?: number;
  result?: any;
}

// Extract base debug info for any error
const getGenericDebugInfo = (error: Error) => ({
  message: error.message,
  name: error.name,
  stack: error.stack,
  errorType: 'Generic' as const,
});

// Extract enhanced debug info for ApolloError
const getApolloDebugInfo = (error: ApolloError) => ({
  ...getGenericDebugInfo(error),
  errorType: 'Apollo' as const,
  // GraphQL errors array with detailed error info
  graphQLErrors: error.graphQLErrors?.map((gqlError) => ({
    message: gqlError.message,
    locations: gqlError.locations, // Line/column in GraphQL query
    path: gqlError.path?.join('.') || 'unknown', // More readable field paths
    extensions: gqlError.extensions, // Custom error data (error codes, etc.)
  })),
  // Network errors (HTTP issues, timeouts, etc.)
  networkError: error.networkError
    ? {
        name: error.networkError.name,
        message: error.networkError.message,
        statusCode: (error.networkError as NetworkErrorWithStatus)?.statusCode,
        result: (error.networkError as NetworkErrorWithStatus)?.result,
      }
    : null,
  // Additional Apollo context
  extraInfo: error.extraInfo,
  // Error subtype classification for Apollo errors
  apolloErrorType: error.graphQLErrors?.length > 0 ? 'GraphQL' : 'Network',
  totalGraphQLErrors: error.graphQLErrors?.length || 0,
});

export const internalError = (error?: Error | ApolloError) => {
  if (!error) {
    return json('', {
      status: 500,
      statusText: 'Internal error',
    });
  }

  // Generate appropriate debug info based on error type
  const debugInfo = isApolloError(error)
    ? getApolloDebugInfo(error)
    : getGenericDebugInfo(error);

  return json(shouldIncludeDebugInfo() ? debugInfo : '', {
    status: 500,
    statusText: error.message,
  });
};
